<template>
  <div v-if="isLoading()" class="loader center-middle">
    <Icon :size="50" class="rotating" icon="loader" color="gamma" />
  </div>
  <ErrorSection v-else-if="isErrored()" />
  <div v-else-if="plan">
    <PageTitle v-if="routeName.toString() === 'plan-detail'">
      <div class="d-flex">
        <div class="pr-2 d-flex">
          <div>{{ getName() }}</div>
          <div @click="changePlanName" class="mx-1" role="button">
            <Icon :size="15" color="epsilon" icon="edit" />
          </div>
        </div>
        <div class="pr-2">
          <Logo v-if="getApp()" :logo="getApp()" :size="LOGO_SIZE" />
        </div>
        <div>
          <Tooltip :content="getLocationTooltip()">
            <Logo v-if="getLocationLogo()" :logo="getLocationLogo()" :size="LOGO_SIZE" />
          </Tooltip>
        </div>
      </div>
      <HorizontalList :items="planItems()" />
      <template #actions>
        <div class="text-right">
          <div class="mb-2">
            <DropDown v-if="hasStencils()" :items="getAddSiteMenuItems()">
              <Btn variant="primary" :disabled="!canAddSite()">
                {{ $t("global.site-add") }}
              </Btn>
            </DropDown>
            <Btn v-else variant="primary" :disabled="!canAddSite()" @click="addSite">
              {{ $t("global.site-add") }}
            </Btn>
            <Btn class="ml-2" variant="primaryOutline" :disabled="!canUpgrade()" @click="upgrade">
              {{ $t("global.upgrade") }}
            </Btn>
          </div>
          <div
            v-if="plan.hasSafeHarbor || (safeHarborAllowedStatus.isLoaded && isSafeHarborAllowed)"
          >
            <DropDown :items="getSafeHarborMenuItems()">
              <Btn
                variant="primaryOutline"
                :disabled="isSafeHarborUpdating || !planStatus.isLoaded"
              >
                {{ $t("global.safe-harbor") }}
              </Btn>
            </DropDown>
          </div>
        </div>
      </template>
    </PageTitle>
    <router-view v-if="showPage()" :key="routePath" />
    <div v-else class="loader center-middle">
      <Icon :size="50" class="rotating" icon="loader" color="gamma" />
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import { Package, Site } from "leatherman-js/models";
import Btn from "@/components/display/Btn";
import DropDown from "@/components/display/DropDown";
import ErrorSection from "@/components/display/ErrorSection";
import FreeTrialPlanUpgradeModal from "@/components/smart/modals/FreeTrialPlanUpgrade";
import HorizontalList from "@/components/display/HorizontalList";
import Icon from "@/components/display/icon/Icon";
import Logo from "@/components/display/Logo";
import NavLink from "@/classes/NavLink";
import PageTitle from "@/components/display/PageTitle";
import PlanChangeName from "@/components/smart/modals/PlanChangeName";
import PlanUpgrade from "@/components/smart/modals/PlanUpgrade";
import SafeHarborDevelopmentHelp from "@/components/smart/modals/SafeHarborDevelopmentHelp";
import SafeHarborMigrationHelp from "@/components/smart/modals/SafeHarborMigrationHelp";
import SafeHarborPurchase from "@/components/smart/modals/SafeHarborPurchase";
import SafeHarborRemove from "@/components/smart/modals/SafeHarborRemove";
import SiteAdd from "@/components/smart/modals/SiteAdd";
import SiteAddSecondary from "@/components/smart/modals/SiteAddSecondary";
import StencilCreateSiteModal from "@/components/smart/modals/StencilCreateSite";
import store from "@/store";
import Tooltip from "@/components/display/Tooltip";

export default {
  name: "PlanDetailPage",
  components: {
    Btn,
    DropDown,
    ErrorSection,
    HorizontalList,
    Icon,
    Logo,
    PageTitle,
    Tooltip,
  },
  props: {
    planId: Number,
  },
  setup(props, { root }) {
    // all available packages
    const isSafeHarborAllowed = computed(() => store.getters["safeHarbor/isAllowed"]);
    const isSafeHarborUpdating = computed(() =>
      store.getters["safeHarbor/isUpdating"](store.getters["plans/getItem"]?.id)
    );
    const packages = computed(() => ({
      list: store.getters["packages/getList"],
      status: store.getters["packages/getListStatus"],
    }));
    const plan = computed(() => store.getters["plans/getItem"]);
    const planStatus = computed(() => store.getters["plans/getItemStatus"]);
    const safeHarborAllowedStatus = computed(() => store.getters["safeHarbor/getAllowedStatus"]);
    const siteStatus = computed(() => store.getters["plans/getPrimarySiteStatus"]);
    const stencils = computed(() => store.getters["allStencils/getList"]);
    const routePath = computed(() => root.$route.fullPath);
    const routeName = computed(
      () => root.$route.matched[root.$route.matched.length - 1].parent.name
    );
    const sites = computed(() => ({
      list: store.getters["sites/getList"],
      status: store.getters["sites/getListStatus"],
    }));

    const getApp = () => getPrimarySite()?.app?.type;

    const getAddSiteMenuItems = () => [
      new NavLink(root.$t("global.create-using-application"), null, addSite, false, "default"),
      new NavLink(
        root.$t("global.create-using-stencil"),
        null,
        addSiteFromStencil,
        false,
        "default"
      ),
    ];

    const getIsFreeTrial = () => getPlan()?.package?.freeTrial;
    const getIsFreeTrialClass = () => (getIsFreeTrial() ? "plan-free-trial" : "");
    const getLocationLogo = () => `location-${getPlan()?.location?.country?.toLowerCase()}`;
    const getLocationTooltip = () => `${getPlan()?.location?.name} ${getPlan()?.location?.country}`;
    const getName = () => getPlan()?.nickname;
    const getPackage = () => Package.getById(getPackages().list, getPlan().package.id);
    const getPackages = () => packages.value;
    const getPlan = () => plan.value;
    const getPrice = () => getPlan()?.billing?.price;
    const getPrimarySite = () => Site.getPlanPrimarySite(getPlan()?.id, getSites()?.list);
    const getRenewalDate = () => getPlan()?.billing?.formattedNextBillDate;
    const changePlanName = () => {
      store.dispatch("modals/open", {
        template: PlanChangeName,
        data: {
          planId: plan.value.id,
          planName: plan.value.nickname,
        },
      });
    };

    const getSafeHarborMenuItems = () => {
      if (plan.value.hasSafeHarbor) {
        return [
          new NavLink(
            root.$t("global.ready-to-migrate"),
            null,
            openMigrationHelp,
            false,
            "default"
          ),
          new NavLink(
            root.$t("global.need-development-help"),
            null,
            openDevelopmentHelp,
            false,
            "info"
          ),
          new NavLink(root.$t("global.remove"), null, openRemove, false, "danger"),
        ];
      }
      return [new NavLink(root.$t("global.add"), null, openPurchase, false, "default")];
    };

    const getSites = () => sites.value;
    const getSitesUsed = () =>
      root.$t("global.plan-sites-used", {
        used: getPlan()?.siteCount,
        allotted: getPlan()?.package?.allottedSites,
      });
    const getType = () => getPlan()?.package?.name;

    const hasStencils = () => stencils.value?.length > 0;

    const LOGO_SIZE = 30;

    // opens the site creation modal
    const addSite = () => {
      const app = getPrimarySite().app;
      const siteId = getPrimarySite().id;

      if (getPlan().isTShirt) {
        store.dispatch("modals/open", {
          template: SiteAddSecondary,
          data: { plan: getPlan(), app, siteId },
        });
      } else {
        store.dispatch("modals/open", {
          template: SiteAdd,
          data: { app, plan: getPlan() },
        });
      }
    };
    // user can add a new site to their plan if they haven't created the maximum amount yet
    const canAddSite = () => {
      if (!getSites().status.isLoaded || !getPackages().status.isLoaded) return false;
      return getPlan().siteCount < getPackage()?.allottedSites;
    };
    // user can upgrade if all resources have been loaded
    const canUpgrade = () => getSites().status.isLoaded && getPackages().status.isLoaded;
    // opens the plan upgrade modal
    const upgrade = () => {
      if (getIsFreeTrial()) {
        store.dispatch("modals/open", {
          template: FreeTrialPlanUpgradeModal,
          data: {
            package: plan.value.package,
            plan: plan.value,
            app: getPrimarySite().app,
          },
        });
      } else {
        store.dispatch("modals/open", {
          template: PlanUpgrade,
          data: {
            isPlanTShirt: plan.value.isTShirt,
            package: plan.value.package,
            planId: plan.value.id,
            planAppId: getPrimarySite().app.id,
            planName: plan.value.nickname,
            planSiteCount: plan.value.siteCount,
            planTerm: plan.value.billing.months,
          },
        });
      }
    };

    const openDevelopmentHelp = () =>
      store.dispatch("modals/open", {
        template: SafeHarborDevelopmentHelp,
        data: { resourceId: getPrimarySite()?.id, type: "site" },
      });

    const openMigrationHelp = () =>
      store.dispatch("modals/open", {
        template: SafeHarborMigrationHelp,
        data: { resourceId: getPrimarySite()?.id, type: "site" },
      });

    const openPurchase = () =>
      store.dispatch("modals/open", {
        template: SafeHarborPurchase,
        data: { service: plan.value },
      });

    const openRemove = () =>
      store.dispatch("modals/open", {
        template: SafeHarborRemove,
        data: { service: plan.value },
      });

    const showPage = () =>
      (planStatus.value.isLoaded || planStatus.value.isUpdating) &&
      (siteStatus.value.isLoaded || siteStatus.value.isUpdating) &&
      !isSafeHarborUpdating.value;

    const isErrored = () => planStatus.value.isErrored || siteStatus.value.isErrored;
    const isLoading = () => planStatus.value.isLoading || siteStatus.value.isLoading;

    const addSiteFromStencil = () => {
      store.dispatch("modals/open", {
        template: StencilCreateSiteModal,
        confirm: () => {
          root.$router.push({
            name: "plan-sites",
            params: {
              planId: plan.value.id,
            },
          });
        },
        data: {
          site: getPrimarySite(),
          plan: plan.value,
        },
      });
    };

    const planItems = () => [
      {
        key: root.$t("global.sites-used"),
        value: getSitesUsed(),
      },
      {
        key: root.$t("global.type"),
        value: getType(),
      },
      {
        key: root.$t(getIsFreeTrial() ? "global.trial-period-expires" : "global.renewal"),
        value: `<span class="${getIsFreeTrialClass()}">${getRenewalDate()}</span>`,
      },
      {
        key: root.$t("global.price"),
        value: getPrice(),
      },
    ];

    onMounted(() => {
      store.dispatch("plans/getItem", props.planId).then((plan) => {
        const id = plan.cloudAccounts[0].id;
        store.dispatch("plans/getPrimarySite", id).then((site) => {
          store.dispatch("safeHarbor/getPlanAllowed", site.id);
        });
      });
      store.dispatch("sites/getList", { planId: props.planId });
      store.dispatch("packages/getList");
      store.dispatch("allStencils/getList", { planId: props.planId });
    });

    return {
      addSite,
      canAddSite,
      canUpgrade,
      getApp,
      getAddSiteMenuItems,
      getIsFreeTrial,
      getIsFreeTrialClass,
      getLocationLogo,
      getLocationTooltip,
      getName,
      getPrice,
      getRenewalDate,
      getSafeHarborMenuItems,
      getSitesUsed,
      getType,
      isErrored,
      isLoading,
      isSafeHarborAllowed,
      isSafeHarborUpdating,
      hasStencils,
      LOGO_SIZE,
      plan,
      planItems,
      planStatus,
      routeName,
      routePath,
      safeHarborAllowedStatus,
      sites,
      siteStatus,
      stencils,
      upgrade,
      showPage,
      changePlanName,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";

.plan-attributes {
  font-size: 0.875rem;
  .attribute {
    display: flex;
    align-items: baseline;
    @include media-breakpoint-up(md) {
      &:not(:last-child) {
        &::after {
          margin-right: 1rem;
          margin-left: 1rem;
          content: "|";
        }
      }
    }
    .field {
      font-size: 0.75rem;
      margin-right: 0.5rem;
    }
    .value {
      font-weight: bold;
    }
  }
}
</style>
